import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Globe, 
  Activity, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  TestTube,
  History,
  Settings,
  Zap
} from 'lucide-react';

const WebhookManager = ({ onShowHelp }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [],
    headers: {},
    retry_count: 3,
    timeout: 10,
    is_active: true
  });
  
  // Testing states
  const [testingWebhook, setTestingWebhook] = useState(null);
  const [testResult, setTestResult] = useState(null);
  
  // History states
  const [showHistory, setShowHistory] = useState(false);
  const [selectedWebhookHistory, setSelectedWebhookHistory] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Guide state
  const [showGuide, setShowGuide] = useState(false);
  
  // Available events for webhooks
  const availableEvents = [
    'agent_self_healing_triggered',
    'agent_prompt_updated',
    'agent_performance_declined',
    'agent_registered',
    'agent_deleted',
    'feedback_submitted',
    'customer_created',
    'customer_updated'
  ];

  // Fetch all webhooks for the user
  const fetchWebhooks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(getApiUrl('/user/webhooks'), {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setWebhooks(response.data.webhooks || []);
      }
    } catch (err) {
      console.error('Error fetching webhooks:', err);
      setError(err.response?.data?.error || 'Failed to fetch webhooks');
    } finally {
      setLoading(false);
    }
  };

  // Create new webhook
  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(getApiUrl('/user/webhooks'), formData, {
        withCredentials: true
      });
      
      if (response.status === 201) {
        setSuccess('Webhook created successfully!');
        setShowCreateForm(false);
        setFormData({
          name: '',
          url: '',
          events: [],
          headers: {},
          retry_count: 3,
          timeout: 10,
          is_active: true
        });
        fetchWebhooks();
      }
    } catch (err) {
      console.error('Error creating webhook:', err);
      setError(err.response?.data?.error || 'Failed to create webhook');
    } finally {
      setLoading(false);
    }
  };

  // Update existing webhook
  const handleUpdateWebhook = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.put(getApiUrl(`/user/webhooks/${editingWebhook.id}`), formData, {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setSuccess('Webhook updated successfully!');
        setShowEditForm(false);
        setEditingWebhook(null);
        setFormData({
          name: '',
          url: '',
          events: [],
          headers: {},
          retry_count: 3,
          timeout: 10,
          is_active: true
        });
        fetchWebhooks();
      }
    } catch (err) {
      console.error('Error updating webhook:', err);
      setError(err.response?.data?.error || 'Failed to update webhook');
    } finally {
      setLoading(false);
    }
  };

  // Delete webhook
  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.delete(getApiUrl(`/user/webhooks/${webhookId}`), {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setSuccess('Webhook deleted successfully!');
        fetchWebhooks();
      }
    } catch (err) {
      console.error('Error deleting webhook:', err);
      setError(err.response?.data?.error || 'Failed to delete webhook');
    } finally {
      setLoading(false);
    }
  };

  // Test webhook
  const handleTestWebhook = async (webhookId) => {
    setTestingWebhook(webhookId);
    setTestResult(null);
    
    try {
      const response = await axios.post(getApiUrl(`/user/webhooks/${webhookId}/test`), {}, {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setTestResult({
          success: true,
          message: 'Webhook test successful!',
          response: response.data
        });
      }
    } catch (err) {
      console.error('Error testing webhook:', err);
      setTestResult({
        success: false,
        message: err.response?.data?.error || 'Webhook test failed',
        response: err.response?.data
      });
    } finally {
      setTestingWebhook(null);
    }
  };

  // Fetch webhook history
  const fetchWebhookHistory = async (webhookId) => {
    setHistoryLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(getApiUrl(`/user/webhooks/${webhookId}/history`), {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setHistoryData(response.data.history || []);
        setSelectedWebhookHistory(webhooks.find(w => w.id === webhookId));
        setShowHistory(true);
      }
    } catch (err) {
      console.error('Error fetching webhook history:', err);
      setError(err.response?.data?.error || 'Failed to fetch webhook history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle edit webhook
  const handleEditWebhook = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events || [],
      headers: webhook.headers || {},
      retry_count: webhook.retry_count || 3,
      timeout: webhook.timeout || 10,
      is_active: webhook.is_active !== false
    });
    setShowEditForm(true);
  };

  // Handle event selection
  const handleEventToggle = (event) => {
    const newEvents = formData.events.includes(event)
      ? formData.events.filter(e => e !== event)
      : [...formData.events, event];
    
    setFormData({ ...formData, events: newEvents });
  };

  // Add header
  const handleAddHeader = () => {
    const key = prompt('Enter header key:');
    const value = prompt('Enter header value:');
    
    if (key && value) {
      setFormData({
        ...formData,
        headers: { ...formData.headers, [key]: value }
      });
    }
  };

  // Remove header
  const handleRemoveHeader = (key) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    setFormData({ ...formData, headers: newHeaders });
  };

  // Copy webhook URL
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-500';
      case 'failed':
        return 'text-red-500';
      case 'pending':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Webhook Management</h1>
            <p className="text-gray-400">Manage your webhooks for real-time notifications and integrations</p>
          </div>
          <div className="flex items-center space-x-4">
            {onShowHelp && (
              <button
                onClick={onShowHelp}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors duration-300"
              >
                <AlertCircle className="w-4 h-4" />
                <span>Help</span>
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center space-x-2 bg-[#7B61FF] text-white px-4 py-2 rounded-lg hover:bg-[#6B51EF] transition-colors duration-300"
            >
              <Plus className="w-4 h-4" />
              <span>Add Webhook</span>
            </button>
            <button
              onClick={fetchWebhooks}
              disabled={loading}
              className="flex items-center space-x-2 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-300"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Webhook Guide */}
        <div className="mb-6 p-4 bg-gray-800 border border-gray-600 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Globe className="w-5 h-5 mr-2 text-blue-400" />
              How Webhooks Work
            </h3>
            <button
              onClick={() => setShowGuide(!showGuide)}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {showGuide ? 'Hide' : 'Show'} Details
            </button>
          </div>
          
          {showGuide && (
            <div className="space-y-4 text-sm text-gray-300">
              <div className="grid md:grid-cols-2 gap-6">
                {/* How It Works */}
                <div>
                  <h4 className="font-semibold text-blue-400 mb-3">🔗 How Webhooks Work</h4>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">1. Event Detection</span>
                        <p className="text-xs text-gray-400">Monitors your AI agents for specific events and activities</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">2. Data Packaging</span>
                        <p className="text-xs text-gray-400">Packages event data with timestamps and context</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">3. HTTP Delivery</span>
                        <p className="text-xs text-gray-400">Sends POST requests to your specified URL</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">4. Retry Logic</span>
                        <p className="text-xs text-gray-400">Automatically retries failed deliveries with exponential backoff</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Value Proposition */}
                <div>
                  <h4 className="font-semibold text-green-400 mb-3">💡 Why Use Webhooks</h4>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-green-400">🚀 Real-time Integration</span>
                        <p className="text-xs text-gray-400">Connect your AI agents to external systems instantly</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-green-400">📊 Analytics & Monitoring</span>
                        <p className="text-xs text-gray-400">Track agent performance and user interactions</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-green-400">🔔 Notifications</span>
                        <p className="text-xs text-gray-400">Get alerts for critical events and issues</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-green-400">🔄 Automation</span>
                        <p className="text-xs text-gray-400">Trigger automated workflows based on agent events</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Available Events */}
              <div className="border-t border-gray-600 pt-4">
                <h4 className="font-semibold text-purple-400 mb-3">📡 Available Events</h4>
                <div className="grid md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="font-medium text-purple-400 mb-2">🤖 Agent Events:</p>
                    <p className="text-gray-400">agent_self_healing_triggered, agent_prompt_updated, agent_performance_declined, agent_registered, agent_deleted</p>
                  </div>
                  <div>
                    <p className="font-medium text-purple-400 mb-2">👥 User Events:</p>
                    <p className="text-gray-400">feedback_submitted, customer_created, customer_updated</p>
                  </div>
                </div>
              </div>

              {/* Best Practices */}
              <div className="border-t border-gray-600 pt-4">
                <h4 className="font-semibold text-orange-400 mb-3">⚡ Best Practices</h4>
                <div className="grid md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="font-medium text-orange-400 mb-2">🔒 Security:</p>
                    <p className="text-gray-400">Use HTTPS endpoints and implement webhook signature verification for production use.</p>
                  </div>
                  <div>
                    <p className="font-medium text-orange-400 mb-2">⚡ Performance:</p>
                    <p className="text-gray-400">Respond quickly (within 5 seconds) to avoid timeout issues and retry loops.</p>
                  </div>
                  <div>
                    <p className="font-medium text-orange-400 mb-2">🔄 Reliability:</p>
                    <p className="text-gray-400">Implement idempotency to handle duplicate webhook deliveries gracefully.</p>
                  </div>
                  <div>
                    <p className="font-medium text-orange-400 mb-2">📝 Logging:</p>
                    <p className="text-gray-400">Log all webhook events for debugging and audit purposes.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-700/50 rounded-xl">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-700/50 rounded-xl">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
              <span className="text-green-300">{success}</span>
            </div>
          </div>
        )}

        {/* Webhooks List */}
        {loading && webhooks.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B61FF]"></div>
          </div>
        ) : webhooks.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Webhooks Yet</h3>
            <p className="text-gray-500 mb-6">Create your first webhook to start receiving real-time notifications</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center space-x-2 bg-[#7B61FF] text-white px-6 py-3 rounded-lg hover:bg-[#6B51EF] transition-colors duration-300 mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>Create Your First Webhook</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="bg-gray-800 border border-gray-600 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center">
                      {webhook.name}
                      {webhook.is_active ? (
                        <span className="ml-2 px-2 py-1 bg-green-900 text-green-300 text-xs rounded-full">Active</span>
                      ) : (
                        <span className="ml-2 px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">Inactive</span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">{webhook.url}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Created {formatDate(webhook.created_at)} • 
                      Last delivery: {webhook.last_delivery ? formatDate(webhook.last_delivery) : 'Never'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => fetchWebhookHistory(webhook.id)}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title="View History"
                    >
                      <History className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testingWebhook === webhook.id}
                      className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                      title="Test Webhook"
                    >
                      {testingWebhook === webhook.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(webhook.url)}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditWebhook(webhook)}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title="Edit Webhook"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      title="Delete Webhook"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Webhook Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Events</h4>
                    <div className="flex flex-wrap gap-1">
                      {webhook.events && webhook.events.length > 0 ? (
                        webhook.events.map((event, index) => (
                          <span key={index} className="px-2 py-1 bg-purple-900 text-purple-300 text-xs rounded">
                            {event}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500 text-sm">No events configured</span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Delivery Stats</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Success Rate:</span>
                        <span className="text-green-400">{webhook.success_rate || 0}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Total Deliveries:</span>
                        <span className="text-white">{webhook.total_deliveries || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Configuration</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Retries:</span>
                        <span className="text-white">{webhook.retry_count || 3}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Timeout:</span>
                        <span className="text-white">{webhook.timeout || 10}s</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Result */}
                {testResult && testingWebhook === webhook.id && (
                  <div className={`mt-4 p-3 rounded-lg ${
                    testResult.success 
                      ? 'bg-green-900/20 border border-green-700/50' 
                      : 'bg-red-900/20 border border-red-700/50'
                  }`}>
                    <div className="flex items-center">
                      {testResult.success ? (
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400 mr-2" />
                      )}
                      <span className={testResult.success ? 'text-green-300' : 'text-red-300'}>
                        {testResult.message}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Webhook Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create New Webhook</h2>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleCreateWebhook}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Webhook Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="My Webhook"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Webhook URL *
                    </label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://your-domain.com/webhook"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Events to Listen For
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {availableEvents.map((event) => (
                        <label key={event} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.events.includes(event)}
                            onChange={() => handleEventToggle(event)}
                            className="rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-300">{event}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Custom Headers (Optional)
                    </label>
                    <div className="space-y-2">
                      {Object.entries(formData.headers).map(([key, value]) => (
                        <div key={key} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={key}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                          />
                          <input
                            type="text"
                            value={value}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveHeader(key)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddHeader}
                        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                      >
                        Add Header
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Retry Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={formData.retry_count}
                        onChange={(e) => setFormData({ ...formData, retry_count: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={formData.timeout}
                        onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-300">Active</span>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-[#7B61FF] text-white rounded-lg hover:bg-[#6B51EF] disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Creating...' : 'Create Webhook'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Webhook Modal */}
        {showEditForm && editingWebhook && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Edit Webhook: {editingWebhook.name}</h2>
                <button
                  onClick={() => setShowEditForm(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleUpdateWebhook}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Webhook Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="My Webhook"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Webhook URL *
                    </label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://your-domain.com/webhook"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Events to Listen For
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {availableEvents.map((event) => (
                        <label key={event} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.events.includes(event)}
                            onChange={() => handleEventToggle(event)}
                            className="rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-300">{event}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Custom Headers (Optional)
                    </label>
                    <div className="space-y-2">
                      {Object.entries(formData.headers).map(([key, value]) => (
                        <div key={key} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={key}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                          />
                          <input
                            type="text"
                            value={value}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveHeader(key)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddHeader}
                        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                      >
                        Add Header
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Retry Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={formData.retry_count}
                        onChange={(e) => setFormData({ ...formData, retry_count: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={formData.timeout}
                        onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-300">Active</span>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditForm(false)}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-[#7B61FF] text-white rounded-lg hover:bg-[#6B51EF] disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Updating...' : 'Update Webhook'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Webhook History Modal */}
        {showHistory && selectedWebhookHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  Delivery History: {selectedWebhookHistory.name}
                </h2>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              {historyLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7B61FF]"></div>
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No delivery history found for this webhook.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historyData.map((delivery) => (
                    <div key={delivery.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(delivery.status)}
                          <div>
                            <div className="text-sm font-medium text-white">
                              {delivery.event_type || 'Unknown Event'}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatDate(delivery.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-medium ${getStatusColor(delivery.status)}`}>
                            {delivery.status}
                          </div>
                          <div className="text-xs text-gray-400">
                            {delivery.response_time ? `${delivery.response_time}ms` : 'N/A'}
                          </div>
                        </div>
                      </div>
                      
                      {delivery.error_message && (
                        <div className="mt-3 p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
                          <div className="text-sm text-red-300">
                            <strong>Error:</strong> {delivery.error_message}
                          </div>
                        </div>
                      )}
                      
                      {delivery.response_body && (
                        <details className="mt-3">
                          <summary className="text-sm text-gray-400 cursor-pointer hover:text-white">
                            View Response Details
                          </summary>
                          <pre className="mt-2 p-3 bg-gray-800 rounded-lg text-xs text-gray-300 overflow-x-auto">
                            {JSON.stringify(JSON.parse(delivery.response_body), null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookManager; 